import { useRef, useState, type FormEvent } from 'react';
import { useCapability } from '../ui/StaffSession';
import { Permissions as P } from '@myfm/domain';
import { assertUploadFile } from '../ui/document-upload-policy';
import { saveRequest } from '../ui/save-request';
import { ErrorState, reviewedApplicationError } from '../ui/State';
import { retainOnboardingSource, ExistingDocumentMatch, type OnboardingSource } from './onboarding-source';
import { useRecordDraft } from '../ui/RecordDraftBoundary';
import { Link } from '../ui/ScopedLink';
export function ConveyancingUpload({
  caseId,
  building,
  serviceId = null,
  title = 'Upload transaction evidence',
  onSaved
}: {
  caseId: string;
  building: string;
  serviceId?: string | null;
  title?: string;
  onSaved: () => void;
}) {
  const allowed = useCapability(P.ManageDocuments),
    [file, setFile] = useState<File>(),
    [busy, setBusy] = useState(false),
    [error, setError] = useState<unknown>(),
    [documentId, setDocumentId] = useState(''),
    proof = useRef<OnboardingSource | undefined>(undefined),
    key = useRef(crypto.randomUUID()),
    lock = useRef(false),
    input = useRef<HTMLInputElement>(null);
  useRecordDraft(Boolean(file) || busy);
  if (!allowed) return null;
  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!file || lock.current) return;
    lock.current = true;
    setBusy(true);
    setError(undefined);
    try {
      await assertUploadFile(file);
      const bytes = await file.arrayBuffer(),
        checksum = Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes)))
          .map((v) => v.toString(16).padStart(2, '0'))
          .join('');
      let existingDocumentId: string | null = null;
      try {
        proof.current = await retainOnboardingSource(file, checksum, building, proof.current, (p) => {
          proof.current = p;
        });
      } catch (e) {
        if (!(e instanceof ExistingDocumentMatch) || !e.linkable) throw e;
        existingDocumentId = e.documentId;
      }
      const r = await saveRequest<{ documentId: string; building: string; caseId: string }>('/api/conveyancing/' + caseId + '/documents', {
        method: 'POST',
        body: JSON.stringify({
          sourceItemId: existingDocumentId ? null : proof.current!.itemId,
          existingDocumentId,
          checksum,
          serviceId,
          reason: 'User retained ' + file.name + ' for this conveyancing transaction',
          idempotencyKey: key.current
        })
      });
      if (r.building !== building || r.caseId !== caseId) throw reviewedApplicationError('The retained document did not match this transaction. Retry the same file.');
      setDocumentId(r.documentId);
      setFile(undefined);
      proof.current = undefined;
      key.current = crypto.randomUUID();
      if (input.current) input.current.value = '';
      onSaved();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
      lock.current = false;
    }
  }
  return (
    <details>
      <summary>{title}</summary>
      <form aria-label={title} onSubmit={submit}>
        <fieldset disabled={busy}>
          <label>
            Original file
            <input
              ref={input}
              type="file"
              required
              onChange={(e) => {
                setFile(e.target.files?.[0]);
                proof.current = undefined;
                key.current = crypto.randomUUID();
              }}
            />
          </label>
          <p>The property and building are retained. Original storage, duplicate checks and malware scanning use the existing document intake. ZIP uploads are not accepted.</p>
          <button>Upload document</button>
        </fieldset>
      </form>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {documentId && (
        <p role="status">
          Saved in Documents. <Link to={'/documents/' + documentId}>Open retained original</Link>
        </p>
      )}
    </details>
  );
}
